import { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calculator, ChevronDown, Zap, ArrowRight, RotateCcw, Share2, Check, Copy } from "lucide-react";
import { screenPrintingTiers, dtfTiers, embroideryTiers, garmentOptions } from "../data/pricing";

const printMethods = [
  { id: "screen", label: "Screen Printing", min: 6 },
  { id: "dtf", label: "DTF Printing", min: 1 },
  { id: "embroidery", label: "Embroidery", min: 12 },
];

const rushOptions = [
  { id: "none", label: "Standard (7-10 days)", add: 0 },
  { id: "5day", label: "5-7 Day Rush", add: 2.00 },
  { id: "3day", label: "3-4 Day Rush", add: 4.00 },
];

function getTier(method, qty) {
  const tiers = method === "screen" ? screenPrintingTiers : method === "dtf" ? dtfTiers : embroideryTiers;
  return tiers.find(t => qty >= t.min && qty <= t.max) || null;
}

export default function QuoteCalculator() {
  const [searchParams] = useSearchParams();
  const [method, setMethod] = useState("screen");
  const [quantity, setQuantity] = useState(48);
  const [colors, setColors] = useState(1);
  const [garmentIdx, setGarmentIdx] = useState(0);
  const [rush, setRush] = useState("none");
  const [garmentOpen, setGarmentOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Restore state from URL params (shared link)
  useEffect(() => {
    const m = searchParams.get("m");
    const q = searchParams.get("q");
    const c = searchParams.get("c");
    const g = searchParams.get("g");
    const r = searchParams.get("r");
    if (m && ["screen", "dtf", "embroidery"].includes(m)) setMethod(m);
    if (q) { const num = parseInt(q, 10); if (num > 0 && num <= 500) setQuantity(num); }
    if (c) { const num = parseInt(c, 10); if (num >= 1 && num <= 6) setColors(num); }
    if (g) { const num = parseInt(g, 10); if (num >= 0 && num < garmentOptions.length) setGarmentIdx(num); }
    if (r && ["none", "5day", "3day"].includes(r)) setRush(r);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const minQty = printMethods.find(m => m.id === method).min;

  const buildShareUrl = useCallback(() => {
    const base = window.location.origin + "/pricing";
    const params = new URLSearchParams();
    params.set("m", method);
    params.set("q", String(Math.max(quantity, minQty)));
    if (method === "screen") params.set("c", String(colors));
    params.set("g", String(garmentIdx));
    if (rush !== "none") params.set("r", rush);
    return `${base}?${params.toString()}`;
  }, [method, quantity, colors, garmentIdx, rush, minQty]);

  const handleShare = async () => {
    const url = buildShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for older browsers
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const estimate = useMemo(() => {
    const qty = Math.max(quantity, minQty);
    const tier = getTier(method, qty);
    if (!tier) return null;

    let printCost = tier.price;
    if (method === "screen" && colors > 1) {
      printCost += (colors - 1) * tier.colorAdd;
    }

    const garment = garmentOptions[garmentIdx];
    const garmentCost = garment.price;
    const rushAdd = rushOptions.find(r => r.id === rush).add;
    const setupFee = method === "screen" && qty < 24 ? 20 * colors : 0;
    const digitizingFee = method === "embroidery" && qty < 24 ? 25 : 0;

    const perPiece = printCost + garmentCost + rushAdd;
    const subtotal = perPiece * qty;
    const total = subtotal + setupFee + digitizingFee;

    return { printCost, garmentCost, rushAdd, perPiece, subtotal, setupFee, digitizingFee, total, qty, tier };
  }, [method, quantity, colors, garmentIdx, rush, minQty]);

  const handleReset = () => {
    setMethod("screen");
    setQuantity(48);
    setColors(1);
    setGarmentIdx(0);
    setRush("none");
    setCopied(false);
    // Clear URL params without navigation
    window.history.replaceState({}, "", window.location.pathname);
  };

  return (
    <div data-testid="quote-calculator" className="relative overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-600 flex items-center justify-center">
            <Calculator size={18} className="text-white" />
          </div>
          <div>
            <h2 className="font-teko text-3xl text-white uppercase tracking-tight leading-none">Instant Quote Calculator</h2>
            <p className="text-xs text-neutral-500 font-inter">Get a ballpark estimate in seconds</p>
          </div>
        </div>
        <button onClick={handleReset} data-testid="calc-reset" className="text-neutral-500 hover:text-white transition-colors p-2" title="Reset">
          <RotateCcw size={16} />
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Controls */}
        <div className="space-y-5">
          {/* Print Method */}
          <div>
            <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-2">Print Method</label>
            <div className="grid grid-cols-3 gap-2">
              {printMethods.map(m => (
                <button
                  key={m.id}
                  data-testid={`calc-method-${m.id}`}
                  onClick={() => { setMethod(m.id); if (quantity < m.min) setQuantity(m.min); if (m.id !== "screen") setColors(1); }}
                  className={`py-3 text-center font-teko text-sm uppercase tracking-wider transition-all ${
                    method === m.id
                      ? "bg-red-600 text-white"
                      : "bg-neutral-900 text-neutral-400 border border-neutral-800 hover:border-neutral-600"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter">Quantity</label>
              <span className="font-teko text-xl text-red-500">{Math.max(quantity, minQty)} pcs</span>
            </div>
            <input
              data-testid="calc-quantity"
              type="range"
              min={minQty}
              max={500}
              value={Math.max(quantity, minQty)}
              onChange={e => setQuantity(Number(e.target.value))}
              className="w-full accent-red-600 h-2 bg-neutral-800 rounded-none appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-red-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:cursor-pointer
                [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:bg-red-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-none"
            />
            <div className="flex justify-between text-[10px] text-neutral-600 font-inter mt-1">
              <span>{minQty}</span>
              <span>100</span>
              <span>250</span>
              <span>500</span>
            </div>
          </div>

          {/* Colors (screen printing only) */}
          {method === "screen" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter">Number of Colors</label>
                <span className="font-teko text-xl text-red-500">{colors} {colors === 1 ? "color" : "colors"}</span>
              </div>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6].map(c => (
                  <button
                    key={c}
                    data-testid={`calc-color-${c}`}
                    onClick={() => setColors(c)}
                    className={`flex-1 py-2.5 font-teko text-lg transition-all ${
                      colors === c
                        ? "bg-red-600 text-white"
                        : "bg-neutral-900 text-neutral-500 border border-neutral-800 hover:border-neutral-600"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Garment */}
          <div className="relative">
            <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-2">Blank Garment</label>
            <button
              data-testid="calc-garment-dropdown"
              onClick={() => setGarmentOpen(!garmentOpen)}
              className="w-full flex items-center justify-between bg-neutral-900 border border-neutral-800 hover:border-neutral-600 px-4 py-3 text-left transition-colors"
            >
              <div>
                <span className="text-sm text-white font-inter">{garmentOptions[garmentIdx].name}</span>
                <span className="text-xs text-neutral-500 font-inter ml-2">
                  {garmentOptions[garmentIdx].price > 0 ? `+$${garmentOptions[garmentIdx].price.toFixed(2)}/pc` : "No charge"}
                </span>
              </div>
              <ChevronDown size={14} className={`text-neutral-500 transition-transform ${garmentOpen ? "rotate-180" : ""}`} />
            </button>
            {garmentOpen && (
              <div className="absolute top-full left-0 right-0 z-20 bg-[#111] border border-neutral-700 max-h-56 overflow-y-auto">
                {garmentOptions.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`calc-garment-${i}`}
                    onClick={() => { setGarmentIdx(i); setGarmentOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 flex items-center justify-between transition-colors ${
                      garmentIdx === i ? "bg-red-900/20 text-red-400" : "text-neutral-400 hover:bg-neutral-900 hover:text-white"
                    }`}
                  >
                    <span className="text-xs font-inter">{g.name}</span>
                    <span className="text-xs text-neutral-600 font-inter shrink-0 ml-2">{g.price > 0 ? `+$${g.price.toFixed(2)}` : "—"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Rush */}
          <div>
            <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-2">Turnaround</label>
            <div className="space-y-1.5">
              {rushOptions.map(r => (
                <button
                  key={r.id}
                  data-testid={`calc-rush-${r.id}`}
                  onClick={() => setRush(r.id)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-all ${
                    rush === r.id
                      ? "bg-red-600/10 border border-red-600/40 text-white"
                      : "bg-neutral-900 border border-neutral-800 text-neutral-400 hover:border-neutral-600"
                  }`}
                >
                  <span className="text-xs font-inter">{r.label}</span>
                  {r.add > 0 && <span className="text-xs text-red-500 font-inter">+${r.add.toFixed(2)}/pc</span>}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Estimate Display */}
        <div>
          <div className="bg-neutral-900 border border-neutral-800 p-6 sticky top-28">
            {estimate ? (
              <>
                <div className="flex items-center gap-2 mb-5">
                  <Zap size={14} className="text-red-500" />
                  <span className="text-xs text-red-500 uppercase tracking-widest font-inter">Your Estimate</span>
                </div>

                {/* Big Total */}
                <div className="text-center py-6 border-b border-neutral-800 mb-5">
                  <div className="text-xs text-neutral-500 font-inter uppercase tracking-widest mb-1">Estimated Total</div>
                  <div className="font-teko text-6xl text-white leading-none" data-testid="calc-total">
                    ${estimate.total.toFixed(2)}
                  </div>
                  <div className="font-teko text-2xl text-red-500 mt-1" data-testid="calc-per-piece">
                    ${estimate.perPiece.toFixed(2)}/piece
                  </div>
                  <div className="text-xs text-neutral-600 font-inter mt-1">{estimate.qty} pieces</div>
                </div>

                {/* Breakdown */}
                <div className="space-y-2.5 mb-6">
                  <div className="flex justify-between text-sm font-inter">
                    <span className="text-neutral-400">
                      Printing ({method === "screen" ? `${colors}-color` : method === "dtf" ? "full color" : "embroidery"})
                    </span>
                    <span className="text-white">${estimate.printCost.toFixed(2)}/pc</span>
                  </div>
                  <div className="flex justify-between text-sm font-inter">
                    <span className="text-neutral-400">Garment ({garmentOptions[garmentIdx].name.split(" ").slice(0, 2).join(" ")})</span>
                    <span className="text-white">{estimate.garmentCost > 0 ? `$${estimate.garmentCost.toFixed(2)}/pc` : "—"}</span>
                  </div>
                  {estimate.rushAdd > 0 && (
                    <div className="flex justify-between text-sm font-inter">
                      <span className="text-neutral-400">Rush fee</span>
                      <span className="text-red-400">+${estimate.rushAdd.toFixed(2)}/pc</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-inter pt-2 border-t border-neutral-800">
                    <span className="text-neutral-400">Subtotal ({estimate.qty} x ${estimate.perPiece.toFixed(2)})</span>
                    <span className="text-white">${estimate.subtotal.toFixed(2)}</span>
                  </div>
                  {estimate.setupFee > 0 && (
                    <div className="flex justify-between text-sm font-inter">
                      <span className="text-neutral-400">Screen setup ({colors} screen{colors > 1 ? "s" : ""})</span>
                      <span className="text-red-400">+${estimate.setupFee.toFixed(2)}</span>
                    </div>
                  )}
                  {estimate.digitizingFee > 0 && (
                    <div className="flex justify-between text-sm font-inter">
                      <span className="text-neutral-400">Digitizing fee</span>
                      <span className="text-red-400">+${estimate.digitizingFee.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Savings Indicator */}
                {estimate.qty >= 48 && (
                  <div className="bg-green-900/20 border border-green-900/40 px-4 py-2.5 mb-5 text-center">
                    <span className="text-green-400 text-xs font-inter uppercase tracking-widest">Volume discount applied</span>
                  </div>
                )}
                {estimate.qty >= 24 && method === "screen" && (
                  <div className="bg-green-900/20 border border-green-900/40 px-4 py-2.5 mb-5 text-center">
                    <span className="text-green-400 text-xs font-inter uppercase tracking-widest">Free setup + Free DFW delivery</span>
                  </div>
                )}

                {/* CTA */}
                <Link
                  to="/contact"
                  data-testid="calc-get-exact-quote"
                  className="ninja-btn-primary w-full text-center py-4 font-teko tracking-widest text-lg flex items-center justify-center gap-2"
                >
                  GET EXACT QUOTE <ArrowRight size={16} />
                </Link>

                {/* Share Quote */}
                <button
                  onClick={handleShare}
                  data-testid="calc-share-quote"
                  className={`w-full mt-2 flex items-center justify-center gap-2 py-3 text-sm font-inter uppercase tracking-widest transition-all ${
                    copied
                      ? "bg-green-900/20 border border-green-600/40 text-green-400"
                      : "border border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-500"
                  }`}
                >
                  {copied ? (
                    <><Check size={14} /> Link Copied!</>
                  ) : (
                    <><Share2 size={14} /> Share This Estimate</>
                  )}
                </button>

                <p className="text-center text-[10px] text-neutral-600 font-inter mt-2">
                  Estimate only. Final pricing confirmed via quote. Free 2-hour response.
                </p>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-neutral-500 font-inter text-sm">
                  {method === "embroidery" ? "Minimum 12 pieces for embroidery" : "Select options to see estimate"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
